import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { CheckIcon, CircleAlertIcon } from "lucide-react";

import SearchParams from "simplydo/core/searchParams";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Message, MessageHeader } from "simplydo/react/ui/message";

import api from "api";
import { useAppSelector } from "store";
import constants from "utils/constants";

import AppStyledDashboard from "components/AppStyledDashboard";
import PageLoadError from "components/lib/PageLoadError";

export const UnsubscribeNotifications = () => {
  const user = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [didUnsubscribe, setDidUnsubscribe] = useState(false);
  const [hasClickedUnsubscribe, setHasClickedUnsubscribe] = useState(false);
  const [error, setError] = useState("");

  const searchParams = new SearchParams(window.location.search);
  const userId = searchParams.has("user")
    ? searchParams.get("user")
    : user?._id;
  const subscription = searchParams.get("subscription");
  const token = searchParams.get("token");

  const { t } = useTranslation();
  const eventInfo = t(
    constants.subscriptions.find((s) => s.key === subscription)?.text || "",
  );

  const unsubscribeFromNotification = useCallback(() => {
    if (!userId || !subscription || !token) {
      return;
    }

    setLoading(true);
    setError("");
    api.users.unsubscribeFromNotification(
      userId,
      subscription,
      token,
      ({ didUnsubscribe: serverDidUnsubscribe }) => {
        setHasClickedUnsubscribe(true);
        setSuccess(true);
        setLoading(false);
        setDidUnsubscribe(serverDidUnsubscribe);
      },
      (err) => {
        setLoading(false);
        setHasClickedUnsubscribe(true);
        setError(err.message);
      },
    );
  }, [userId, subscription, token]);

  if (!eventInfo) {
    return (
      <AppStyledDashboard
        excludeWelcomeText
        content={
          <div>
            <Message color="red">
              We could not unsubscribe you from this type of email. Please
              contact an administrator.
            </Message>
            {!user ? (
              <>
                <Divider />
                <p>
                  You can still log in to view your account or sign up below.
                </p>
                <ButtonLink
                  style={{ marginBottom: 5 }}
                  color="primary"
                  to={`/login`}
                >
                  {t("entry.login.loginOrSignUp")}
                </ButtonLink>
              </>
            ) : null}
          </div>
        }
      />
    );
  }

  if (!userId && !user?._id) {
    return (
      <PageLoadError title={t("notifications.unsubscribe.requiresLogin")} />
    );
  }

  return (
    <AppStyledDashboard
      excludeWelcomeText
      content={
        <div>
          {!hasClickedUnsubscribe ? (
            <div>
              <H3>
                {t("notifications.unsubscribe.title")}
                <Subheader>
                  <p>
                    {t("notifications.unsubscribe.description")}{" "}
                    <b>&quot;{eventInfo}&quot;</b>
                  </p>
                  <p>{t("notifications.unsubscribe.confirm")}</p>
                </Subheader>
              </H3>

              <Button onClick={() => unsubscribeFromNotification()}>
                {t("notifications.unsubscribe.action")}
              </Button>
            </div>
          ) : (
            <div>
              {loading ? (
                <Loader />
              ) : (
                <div>
                  {success ? (
                    didUnsubscribe ? (
                      <Message color="green">
                        <MessageHeader>
                          <Icon icon={CheckIcon} />
                          {t("notifications.unsubscribe.success")}{" "}
                          <b>&quot;{eventInfo}&quot;</b>
                        </MessageHeader>
                      </Message>
                    ) : (
                      <Message>
                        <MessageHeader>
                          <Icon icon={CircleAlertIcon} />
                          {t(
                            "notifications.unsubscribe.alreadyUnsubscribed",
                          )}{" "}
                          <b>&quot;{eventInfo}&quot;</b>
                        </MessageHeader>
                      </Message>
                    )
                  ) : (
                    <Message color="red">{error}</Message>
                  )}

                  <div>
                    {user ? (
                      <ButtonLink
                        variant="outline"
                        to="/preferences/notifications"
                      >
                        Review all notification preferences
                      </ButtonLink>
                    ) : null}
                    <ButtonLink color="primary" to="/">
                      {t("generic.done")}
                    </ButtonLink>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      }
    />
  );
};

export default UnsubscribeNotifications;
